import * as React from "react";
import { createPortal } from "react-dom";
import { SlidersHorizontal } from "lucide-react";
import { cn } from "../../lib/utils";
import { useFocusTrap } from "./useFocusTrap";
import { useOverlayDismiss } from "./useOverlayDismiss";

/**
 * AnchoredPanel — a portalled, anchored, dismissable panel under a trigger.
 *
 * <p>The positioning is {@link OverflowMenu}'s, verbatim in spirit: portalled
 * to `document.body` (a `Table` sets `overflow-x-auto` unconditionally, and a
 * clipping ancestor cannot be escaped with `z-index`), positioned `fixed`
 * against the trigger's rect, measured BEFORE paint, flipped above when below
 * does not fit, clamped to the viewport, and tracking `scroll` (capture phase)
 * and `resize`.</p>
 *
 * <p>Unlike the menu it is a `role="dialog"` — the content is a form (chips, a
 * select, a checklist), not a list of commands — so it runs `useFocusTrap`
 * over its body and takes Escape through `useOverlayDismiss`, which puts it on
 * the same stack as `Modal` and the drawers: Escape closes the panel and not
 * whatever it was opened over.</p>
 */
export interface AnchoredPanelProps {
  /** Renders the trigger. Receives the open state and the ref/handlers to spread. */
  trigger: (args: {
    open: boolean;
    ref: React.RefObject<HTMLButtonElement | null>;
    toggle: () => void;
  }) => React.ReactNode;
  /** Accessible name of the dialog. */
  label: string;
  children: React.ReactNode;
  /** Right-align the panel to the trigger (default) or left-align it. */
  align?: "left" | "right";
  className?: string;
  "data-testid"?: string;
}

export function AnchoredPanel({
  trigger,
  label,
  children,
  align = "left",
  className,
  "data-testid": testId,
}: AnchoredPanelProps) {
  const [open, setOpen] = React.useState(false);
  const triggerRef = React.useRef<HTMLButtonElement>(null);
  const panelRef = React.useRef<HTMLDivElement>(null);
  const [pos, setPos] = React.useState<{ top: number; left: number } | null>(null);

  const close = React.useCallback(() => {
    setOpen(false);
    setPos(null);
  }, []);

  const trap = useFocusTrap<HTMLDivElement>({ active: open });
  useOverlayDismiss(open, close);

  // The Tab cycle, wired as a native listener on the body rather than as a
  // React prop: a static `<div onKeyDown>` is what the design-system's a11y
  // lint rejects, and the trap's handler reads only `key`, `shiftKey` and
  // `preventDefault`, which the native event carries identically.
  const { onKeyDown: trapKeyDown } = trap;
  React.useEffect(() => {
    if (!open) return;
    const el = trap.containerRef.current;
    if (!el) return;
    const handler = (e: KeyboardEvent) =>
      trapKeyDown(e as unknown as React.KeyboardEvent<HTMLElement>);
    el.addEventListener("keydown", handler);
    return () => el.removeEventListener("keydown", handler);
  }, [open, trapKeyDown, trap.containerRef]);

  const reposition = React.useCallback(() => {
    const anchor = triggerRef.current;
    const panel = panelRef.current;
    if (!anchor || !panel) return;
    const rect = anchor.getBoundingClientRect();
    const { offsetWidth: width, offsetHeight: height } = panel;
    const gap = 4;
    const fitsBelow = rect.bottom + gap + height <= window.innerHeight;
    const fitsAbove = rect.top - gap - height >= 0;
    const top = !fitsBelow && fitsAbove ? rect.top - gap - height : rect.bottom + gap;
    const preferred = align === "right" ? rect.right - width : rect.left;
    const left = Math.max(gap, Math.min(preferred, window.innerWidth - width - gap));
    setPos((prev) => (prev && prev.top === top && prev.left === left ? prev : { top, left }));
  }, [align]);

  React.useLayoutEffect(() => {
    if (!open) return;
    reposition();
    const onViewportChange = () => reposition();
    window.addEventListener("scroll", onViewportChange, true);
    window.addEventListener("resize", onViewportChange);
    return () => {
      window.removeEventListener("scroll", onViewportChange, true);
      window.removeEventListener("resize", onViewportChange);
    };
  }, [open, reposition]);

  // Outside click. `mousedown`, so a click that starts inside and ends outside
  // (a drag off a chip) does not dismiss.
  React.useEffect(() => {
    if (!open) return;
    function onDocMouseDown(e: MouseEvent) {
      const target = e.target as Node;
      if (panelRef.current?.contains(target) || triggerRef.current?.contains(target)) return;
      close();
    }
    document.addEventListener("mousedown", onDocMouseDown);
    return () => document.removeEventListener("mousedown", onDocMouseDown);
  }, [open, close]);

  return (
    <>
      {trigger({ open, ref: triggerRef, toggle: () => (open ? close() : setOpen(true)) })}
      {open &&
        createPortal(
          <div
            ref={panelRef}
            role="dialog"
            aria-label={label}
            aria-modal="false"
            style={{ top: pos?.top ?? 0, left: pos?.left ?? 0 }}
            // `min-w-60` (15rem) is Tailwind's scale, the `min-w-36` shape its
            // siblings use — no spacing token reaches a panel-width minimum.
            className={cn(
              "fixed z-[55] min-w-60 max-w-[min(90vw,32rem)] rounded-md border border-border-light bg-white-pure p-space-4 shadow-lg",
              !pos && "invisible",
              className,
            )}
            data-testid={testId}
          >
            {/* The trap's container is the body, not the dialog — the same
                split `Modal` makes, so the Tab cycle is scoped to the content. */}
            <div ref={trap.containerRef}>{children}</div>
          </div>,
          document.body,
        )}
    </>
  );
}

/**
 * "More filters (N)" — the toolbar's second-row disclosure for the filter
 * vocabularies that do not earn a place at rest.
 *
 * <p>The count on the button is the number of filters currently applied
 * INSIDE it, so a reader can see the list is narrowed without opening it. The
 * host renders whatever controls it wants as `children`; this component owns
 * the trigger, the panel and the dismissal, nothing about the filters.</p>
 */
export interface MoreFiltersPanelProps {
  children: React.ReactNode;
  /** How many of the filters behind the button are currently applied. */
  activeCount: number;
  label?: string;
  className?: string;
  "data-testid"?: string;
}

export function MoreFiltersPanel({
  children,
  activeCount,
  label = "More filters",
  className,
  "data-testid": testId,
}: MoreFiltersPanelProps) {
  const text = activeCount > 0 ? `${label} (${activeCount})` : label;
  return (
    <AnchoredPanel
      label={label}
      align="right"
      className={className}
      data-testid={testId ? `${testId}-panel` : undefined}
      trigger={({ open, ref, toggle }) => (
        <button
          ref={ref}
          type="button"
          aria-haspopup="dialog"
          aria-expanded={open}
          onClick={toggle}
          data-testid={testId}
          className={cn(
            "inline-flex h-10 items-center gap-space-2 rounded-md border px-space-3 text-small",
            "transition-colors duration-fast ease-standard focus-visible:outline-none focus-visible:shadow-goldRing",
            activeCount > 0
              ? "border-gold bg-gold/10 font-semibold text-onyx"
              : "border-border-light bg-white-pure text-onyx hover:bg-white-warm",
          )}
        >
          <SlidersHorizontal size={14} strokeWidth={1.5} aria-hidden />
          {text}
        </button>
      )}
    >
      {children}
    </AnchoredPanel>
  );
}
